import { useMemo, useState } from 'react';
import { Mail, Loader2, User, Building2, Store } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { EntityType } from '@peles/shared';
import { useEntityLinks } from '../../hooks/useEntityLinks';
import { useUsers } from '../../hooks/useUsers';
import * as eventsApi from '../../api/events';
import { EntityModal } from '../common/EntityModal';

/** Which vendors the list names. */
export type VendorListScope = 'all' | 'selected';

interface SendVendorListModalProps {
  eventId: string;
  /** The event's vendors, in the event's order. */
  vendors: Array<{ vendorId: string; vendorName: string }>;
  /** Vendors ticked on the list when the modal opened — the starting choice. */
  initialSelectedVendorIds?: string[];
  eventName: string;
  eventDate: string;
  venueId: string | null | undefined;
  currentUser: { email: string; name: string };
  onClose: () => void;
}

export function SendVendorListModal({
  eventId,
  vendors,
  initialSelectedVendorIds = [],
  eventName,
  eventDate,
  venueId,
  currentUser,
  onClose,
}: SendVendorListModalProps) {
  const [selectedContactEmails, setSelectedContactEmails] = useState<string[]>([]);
  const [includeSelf, setIncludeSelf] = useState(true);
  // Vendors ticked on the list mean "these ones"; nothing ticked means all.
  // Only ticks still on the event count — one removed since is not a choice.
  const [chosenVendorIds, setChosenVendorIds] = useState<Set<string>>(() => {
    const onEvent = new Set(vendors.map((vendor) => vendor.vendorId));
    return new Set(initialSelectedVendorIds.filter((vendorId) => onEvent.has(vendorId)));
  });
  const [scope, setScope] = useState<VendorListScope>(() =>
    chosenVendorIds.size > 0 ? 'selected' : 'all',
  );

  const { data: contactLinks = [], isLoading: loadingContacts } = useEntityLinks({
    sourceEntityType: EntityType.VENUE,
    sourceEntityId: venueId ?? '',
    linkType: 'CONTACT',
  });
  const { data: users = [] } = useUsers({ isActive: true });

  const venueContacts = useMemo(() => {
    const userMap = new Map(users.map((u) => [u.id, u]));
    return contactLinks
      .map((link) => {
        const user = userMap.get(link.targetEntityId);
        const meta = (link.metadata || {}) as Record<string, unknown>;
        return {
          id: link.id,
          userName: link.targetEntityName || user?.name || 'Unknown',
          userEmail: user?.email || '',
          title: (meta.title as string) || '',
        };
      })
      .filter((c) => !!c.userEmail);
  }, [contactLinks, users]);

  const sendMutation = useMutation({
    mutationFn: (input: { recipientEmails: string[]; vendorIds?: string[] }) =>
      eventsApi.sendVendorListEmail(eventId, { ...input, replyTo: currentUser.email }),
    onSuccess: onClose,
  });

  // In the event's order, and only vendors still on the event.
  const listedVendorIds = vendors
    .map((vendor) => vendor.vendorId)
    .filter((vendorId) => chosenVendorIds.has(vendorId));

  const toggleVendor = (vendorId: string) => {
    setChosenVendorIds((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) next.delete(vendorId);
      else next.add(vendorId);
      return next;
    });
  };

  const toggleContact = (email: string) => {
    setSelectedContactEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
    );
  };

  const handleSend = () => {
    const recipients = new Set<string>(selectedContactEmails);
    if (includeSelf) recipients.add(currentUser.email);
    if (recipients.size === 0 || noVendorsChosen) return;
    sendMutation.mutate({
      recipientEmails: Array.from(recipients),
      ...(scope === 'selected' ? { vendorIds: listedVendorIds } : {}),
    });
  };

  const recipientCount = new Set([
    ...selectedContactEmails,
    ...(includeSelf ? [currentUser.email] : []),
  ]).size;

  const noVendorsChosen = scope === 'selected' && listedVendorIds.length === 0;

  const subject = `Vendor list for ${eventName} - ${eventDate}`;

  return (
    <EntityModal
      open
      onClose={onClose}
      title="Send Vendor List"
      size="sm"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          {sendMutation.isError && (
            <p className="text-xs text-red-600 flex-1">
              {sendMutation.error instanceof Error
                ? sendMutation.error.message
                : 'Failed to send email'}
            </p>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              onClick={onClose}
              className="min-h-11 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={recipientCount === 0 || noVendorsChosen || sendMutation.isPending}
              className="min-h-11 flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {sendMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Mail size={14} />
              )}
              Send to {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Email subject preview */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Subject</p>
          <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 break-words">
            {subject}
          </p>
        </div>

        {/* Which vendors the list names */}
        <fieldset>
          <legend className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
            Vendors on the list
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(
              [
                { value: 'all', label: 'All vendors', count: vendors.length },
                { value: 'selected', label: 'Selected vendors', count: listedVendorIds.length },
              ] as const
            ).map((option) => (
              <label
                key={option.value}
                className={`min-h-11 flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  scope === option.value
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="vendor-list-scope"
                  value={option.value}
                  checked={scope === option.value}
                  onChange={() => setScope(option.value)}
                  className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-900">{option.label}</span>
                <span className="ml-auto text-xs text-gray-500">{option.count}</span>
              </label>
            ))}
          </div>

          {scope === 'selected' &&
            (vendors.length === 0 ? (
              <p className="mt-2 text-sm text-gray-400 italic px-1">
                No vendors added to this event yet.
              </p>
            ) : (
              <div className="mt-2 max-h-60 overflow-y-auto space-y-1 rounded-xl border border-gray-200 p-1">
                {vendors.map((vendor) => (
                  <label
                    key={vendor.vendorId}
                    className="min-h-11 flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={chosenVendorIds.has(vendor.vendorId)}
                      onChange={() => toggleVendor(vendor.vendorId)}
                      className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
                    />
                    <Store size={15} className="text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-900 truncate">{vendor.vendorName}</span>
                  </label>
                ))}
              </div>
            ))}
          {noVendorsChosen && vendors.length > 0 && (
            <p className="mt-2 text-xs text-amber-700">Choose at least one vendor.</p>
          )}
        </fieldset>

        {/* Send to myself */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Sender</p>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={includeSelf}
              onChange={(e) => setIncludeSelf(e.target.checked)}
              className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
            />
            <User size={15} className="text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">{currentUser.name}</p>
              <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
            </div>
            <span className="ml-auto text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full flex-shrink-0">
              You
            </span>
          </label>
        </div>

        {/* Venue contacts */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
            Venue Contacts
            {venueContacts.length > 0 && (
              <span className="ml-1 text-gray-400 normal-case">({venueContacts.length})</span>
            )}
          </p>

          {!venueId ? (
            <p className="text-sm text-gray-400 italic px-1">No venue linked to this event.</p>
          ) : loadingContacts ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
              <Loader2 size={14} className="animate-spin" />
              Loading contacts…
            </div>
          ) : venueContacts.length === 0 ? (
            <p className="text-sm text-gray-400 italic px-1">No contacts found for this venue.</p>
          ) : (
            <div className="space-y-2">
              {venueContacts.map((contact) => (
                <label
                  key={contact.id}
                  className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedContactEmails.includes(contact.userEmail)}
                    onChange={() => toggleContact(contact.userEmail)}
                    className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
                  />
                  <Building2 size={15} className="text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{contact.userName}</p>
                    <p className="text-xs text-gray-500 truncate">{contact.userEmail}</p>
                  </div>
                  {contact.title && (
                    <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
                      {contact.title}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </EntityModal>
  );
}
